// External libraries
import { FormattedMessage, useIntl } from 'react-intl';
import { useContext, useEffect, useState } from 'react';
import { Button } from '@mui/material';

// API and data fetching
import {
  useUpdateChatSettingsMutation,
  useUpdateTimezoneMutation,
  useUpdateReviewAutomationSettingMutation,
} from '../../api/storeAPI';
import getStoresConfig from '../../api/getStoresConfig';
import { getData, getDataByUrl } from '../../modules/fetch';

// Context and hooks
import { WampHandlersContext } from '../../context/WampHandlersContext';
import { JobsStateContext } from '../../context/JobsStateContext';
import useReviewSettings from '../../hooks/useReviewSettings';

// Components
import ChatSettings from './chatSettings';
import ReviewAutomationSettings from './reviewAutomationSettings';
import Timezone from './timezone';
import ObjectTabs from './objectTabs';
import Popup from '../Popup';
import JobStateToString from '../JobState';

// Utilities
import { toTitleCase } from '../../modules/string';

// Styled components (keep at the end)
import {
  ButtonClear,
  Buttons,
  ButtonSave,
  ButtonSaveTitleSmall,
  ButtonStyledListItem,
  ButtonTitle,
  ButtonTitleSmall,
  Container,
  InnerContainer,
  InnerStyledList,
  InnerStyledListRow,
  StyledDetailsPopupItem,
  StyledListItemText,
  StyledSubListItemText,
  StyledTextField,
} from './style';

const StoreDetailsComponent = ({ object: store }) => {
  const intl = useIntl();
  const [jobsState, setJobsState] = useContext<any>(JobsStateContext);
  const [, setWampHandlers] = useContext<any>(WampHandlersContext);
  const [toJobState, setToJobState] = useState<any>({});
  const [authUrls, setAuthUrls] = useState<any>({});
  const [timezones, setTimezones] = useState<any>([]);
  const [offset, setOffset] = useState<any>(store?.basic?.timezone?.offset);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [chatCutoffPeriod, setChatCutoffPeriod] = useState<any>({
    start: store?.chatSettings?.chatCutoffPeriod?.start ?? '00:00',
    end: store?.chatSettings?.chatCutoffPeriod?.end ?? '23:59',
  });
  const [chatSessionEndTime, setChatSessionEndTime] = useState<string>(
    store?.chatSettings?.chatSessionEndTime ?? 12 * 3600,
  );

  const { settings: reviewSettings, updateSetting: updateReviewSetting } = useReviewSettings(store);
  const [showReviewTemplate, setShowReviewTemplate] = useState(false);

  const [updateTimezone] = useUpdateTimezoneMutation();
  const [updateChatSettings] = useUpdateChatSettingsMutation();
  const [updateReviewAutomationSetting] = useUpdateReviewAutomationSettingMutation();

  let wampIsSet = false;

  useEffect(() => {
    if (!wampIsSet) {
      setWampHandlers(wampHandlers);
      wampIsSet = true;
    }
  }, []);

  useEffect(() => {
    getStoresConfig((config) => {
      const connections = config[store?.basic?.provider];
      setAuthUrls(connections);
    });
    getData('/utility/timezones')
      .then((data) => {
        if (data) setTimezones(data.data);
      })
      .catch((err) => {
        console.error(err);
      });
    setTimezone(store?.basic?.timezone?.name ?? null);
    setOffset(store?.basic?.timezone?.offset ?? null);
    setChatCutoffPeriod({
      start: store?.chatSettings?.chatCutoffPeriod?.start ?? '00:00',
      end: store?.chatSettings?.chatCutoffPeriod?.end ?? '23:59',
    });
    setChatSessionEndTime(store?.chatSettings?.chatSessionEndTime ?? 12 * 3600);
  }, [store]);

  const setNewTimezone = async () => {
    if (timezone) {
      await updateTimezone({
        id: store?._id?.$oid,
        name: timezone,
      }).then((response: any) => {
        if (response?.data?.data) {
          store.basic.timezone = response?.data?.data;
          setTimezone(store?.basic?.timezone?.name ?? null);
          setOffset(store?.basic?.timezone?.offset ?? null);
        }
      });
    } else {
      alert(
        intl.formatMessage({
          id: 'storeDetails.timezone.not.specified',
          defaultMessage: 'Please choose timezone',
        }),
      );
    }

    return true;
  };

  const setChatSettings = async () => {
    await updateChatSettings({
      id: store?._id?.$oid,
      chatCutoffPeriod: chatCutoffPeriod,
      chatSessionEndTime: chatSessionEndTime,
    }).then((response: any) => {
      if (response?.data?.data) {
        setChatCutoffPeriod({
          start: response?.data?.data?.chatCutoffPeriod?.start,
          end: response?.data?.data?.chatCutoffPeriod?.end,
        });
        setChatSessionEndTime(String(response?.data?.data?.chatSessionEndTime));
      }
    });
  };

  const setReviewAutomationSettings = async () => {
    await updateReviewAutomationSetting({
      id: store?._id?.$oid,
      reviewsAutoReplyEnglishTranslation: reviewSettings.reviewsAutoReplyEnglishTranslation,
      reviewAutoReplyEligibleRating: reviewSettings.reviewAutoReplyEligibleRating,
      useReplyTemplate: reviewSettings.useReplyTemplate,
      replyTemplate: reviewSettings.replyTemplate,
    }).then((response: any) => {
      if (response?.data?.data) {
        updateReviewSetting(
          'reviewsAutoReplyEnglishTranslation',
          response?.data?.data?.reviewsAutoReplyEnglishTranslation,
        );
        updateReviewSetting(
          'reviewAutoReplyEligibleRating',
          response?.data?.data?.reviewAutoReplyEligibleRating,
        );
        updateReviewSetting(
          'useReplyTemplate',
          response?.data?.data?.reviewsAutoReplyConfiguration?.useReplyTemplate,
        );
        updateReviewSetting(
          'replyTemplate',
          response?.data?.data?.reviewsAutoReplyConfiguration?.replyTemplate,
        );
      }
    });
  };

  useEffect(() => {
    setJobsState({ ...jobsState, ...toJobState });
  }, [toJobState]);

  const wampHandlers = {
    job: (message: any) => {
      if (message?.job) {
        const job = message.job;
        const name = job?.name ?? 'unknown';
        const storeId = job?.storeId ?? null;
        let states = { [name]: {} };
        if (storeId !== null) {
          states[name][storeId] = job;
          setToJobState({ ...states });
        }
      }
    },
  };

  const jnProducts = 'store_products_re_sync';
  const jnOrders = 'store_orders_re_sync';
  const jnChats = 'store_customers_re_sync';

  // const updateJobsState = storeId => {
  //   setTimeout(async () => {
  //     const retrievedJobsState = { ...(await getJobState(storeId)) };
  //     setJobsState({ ...jobsState, ...retrievedJobsState });
  //   });
  // };

  const runProducts = (storeId) => () => {
    getData(`/stores/resync/products/${storeId}`);

    return true;
  };

  const runOrders = (storeId) => () => {
    getData(`/stores/resync/orders/${storeId}`);

    return true;
  };

  const runChats = (storeId) => () => {
    getData(`/stores/resync/chats/${storeId}`);

    return true;
  };

  const storeId = store?._id?.$oid;

  const disabledProducts = !(
    !jobsState?.[jnProducts] ||
    !jobsState?.[jnProducts]?.[storeId] ||
    jobsState?.[jnProducts]?.[storeId]?.state === 'finished'
  );
  const disabledOrders = !(
    !jobsState?.[jnOrders] ||
    !jobsState?.[jnOrders]?.[storeId] ||
    jobsState?.[jnOrders]?.[storeId]?.state === 'finished'
  );
  const disabledChats = !(
    !jobsState?.[jnChats] ||
    !jobsState?.[jnChats]?.[storeId] ||
    jobsState?.[jnChats]?.[storeId]?.state === 'finished'
  );

  const handleRedirect = (storeAuthUrl) => {
    getDataByUrl(storeAuthUrl)
      .then((data) => {
        window.location = data.data.redirect;
      })
      .catch((err) => {
        console.error(err);
      });

    return true;
  };

  return (
    <Container>
      <>
        <InnerContainer>
          <InnerStyledListRow>
            <Timezone
              timezones={timezones}
              timezone={timezone}
              offset={offset}
              setTimezone={setTimezone}
              onSave={setNewTimezone}
            />
          </InnerStyledListRow>
          <InnerStyledListRow>
            <ChatSettings
              chatCutoffPeriod={chatCutoffPeriod}
              setChatCutoffPeriod={setChatCutoffPeriod}
              chatSessionEndTime={chatSessionEndTime}
              setChatSessionEndTime={setChatSessionEndTime}
              onSave={setChatSettings}
            />
          </InnerStyledListRow>
          <InnerStyledListRow>
            <ReviewAutomationSettings
              settings={reviewSettings}
              updateSetting={updateReviewSetting}
              setShowReviewTemplate={setShowReviewTemplate}
              onSave={setReviewAutomationSettings}
            />
          </InnerStyledListRow>
        </InnerContainer>
        {store?.basic?.provider !== 'whatsapp' && (
          <>
            <Buttons>
              <InnerStyledListRow>
                {authUrls &&
                  Object.keys(authUrls || {}).map((app, i) => {
                    return (
                      <ButtonTitleSmall key={`button_title-handleRedirect-${i}`}>
                        <Button
                          onClick={() => handleRedirect(authUrls[app]?.auth_url)}
                          color="secondary"
                        >
                          <FormattedMessage
                            id={`storeDetails.button.reconnect.${authUrls[app]?.app_id}`}
                            defaultMessage="Reconnect {name}"
                            values={{ name: toTitleCase(authUrls[app]?.name) }}
                          />
                        </Button>
                      </ButtonTitleSmall>
                    );
                  })}
              </InnerStyledListRow>
            </Buttons>
            <Buttons>
              <InnerStyledList>
                <ButtonTitle>
                  <Button
                    onClick={runProducts(storeId)}
                    disabled={disabledProducts}
                    color="secondary"
                  >
                    <FormattedMessage
                      id="storeDetails.button.sync.products"
                      defaultMessage="Syncronize products"
                    />
                  </Button>
                </ButtonTitle>
                <ButtonStyledListItem>
                  <StyledListItemText>
                    <FormattedMessage
                      id="storeDetails.button.sync.status"
                      defaultMessage="Status:"
                    />
                    <div>{JobStateToString(jobsState, jnProducts, storeId, intl) ?? 'N/A'}</div>
                  </StyledListItemText>
                </ButtonStyledListItem>
              </InnerStyledList>
              <InnerStyledList>
                <ButtonTitle>
                  <Button onClick={runOrders(storeId)} disabled={disabledOrders} color="secondary">
                    <FormattedMessage
                      id="storeDetails.button.sync.orders"
                      defaultMessage="Syncronize orders"
                    />
                  </Button>
                </ButtonTitle>
                <ButtonStyledListItem>
                  <StyledListItemText>
                    <FormattedMessage
                      id="storeDetails.button.sync.status"
                      defaultMessage="Status:"
                    />
                    <div>{JobStateToString(jobsState, jnOrders, storeId, intl) ?? 'N/A'}</div>
                  </StyledListItemText>
                </ButtonStyledListItem>
              </InnerStyledList>
              <InnerStyledList>
                <ButtonTitle>
                  <Button onClick={runChats(storeId)} disabled={disabledChats} color="secondary">
                    <FormattedMessage
                      id="storeDetails.button.sync.chats"
                      defaultMessage="Syncronize chats"
                    />
                  </Button>
                </ButtonTitle>
                <ButtonStyledListItem>
                  <StyledListItemText>
                    <FormattedMessage
                      id="storeDetails.button.sync.status"
                      defaultMessage="Status:"
                    />
                    <StyledSubListItemText>
                      <div>{JobStateToString(jobsState, jnChats, storeId, intl) ?? 'N/A'}</div>
                    </StyledSubListItemText>
                  </StyledListItemText>
                </ButtonStyledListItem>
              </InnerStyledList>
            </Buttons>
          </>
        )}

        <ObjectTabs object={store} />

        {showReviewTemplate && (
          <Popup
            isPreviewTicket={false}
            setIsPreviewTicket={() => {}}
            handleClose={() => setShowReviewTemplate(false)}
          >
            <StyledDetailsPopupItem>
              <StyledTextField
                label="Review Auto Reply Template"
                multiline
                rows={4}
                value={reviewSettings.replyTemplate}
                placeholder="auto reply template content that will be used to reply the reviews"
                helperText={`${reviewSettings.replyTemplate?.length || 0}/500 characters`}
                error={reviewSettings.replyTemplate?.length > 500}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    updateReviewSetting('replyTemplate', e.target.value);
                  }
                }}
              />
            </StyledDetailsPopupItem>
            <ButtonSaveTitleSmall
              style={{ display: 'flex', justifyContent: 'center', columnGap: '20px' }}
            >
              <ButtonClear onClick={() => updateReviewSetting('replyTemplate', '')}>
                Clear Template
              </ButtonClear>
              <ButtonSave onClick={() => setShowReviewTemplate(false)}>Done</ButtonSave>
            </ButtonSaveTitleSmall>
          </Popup>
        )}
      </>
    </Container>
  );
};

export default StoreDetailsComponent;
